// src/components/suppliers/SupplierSummaryCards.js
import React, { useMemo, useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Select, Space, Button, Typography } from 'antd';
import { ShoppingCartOutlined, DollarOutlined, FileTextOutlined, UserOutlined, InboxOutlined, CheckSquareOutlined, ExclamationCircleOutlined, GiftOutlined } from '@ant-design/icons';
import './SupplierSummaryCards.css';

const { Text } = Typography;

const { Option } = Select;

const SupplierSummaryCards = ({ summary, orders, suppliers, selectedSupplierId, onSupplierChange, onViewSupplier, loading = false, isDemoMode = false }) => {
    const [selectedMonth, setSelectedMonth] = useState('current');
    
    // Debug logging
    useEffect(() => {
        console.log('SupplierSummaryCards - Props:', { summary, ordersCount: orders?.length, selectedSupplierId });
    }, [summary, orders, selectedSupplierId]);
    
    // Change month selector based on supplier selection
    useEffect(() => {
        setSelectedMonth('current');
    }, [selectedSupplierId]);

    // Generate list of available months from orders
    const availableMonths = useMemo(() => {
        if (!orders || orders.length === 0) return [];

        const monthsSet = new Set();
        orders.forEach(order => {
            if (order.order_date) {
                const date = new Date(order.order_date);
                const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                monthsSet.add(monthKey);
            }
        });

        const monthsList = Array.from(monthsSet).sort().reverse();
        return monthsList;
    }, [orders]);

    // Calculate monthly data from orders array
    const monthlyData = useMemo(() => {
        if (!orders || orders.length === 0) return {};

        const grouped = {};

        orders.forEach(order => {
            if (!order.order_date) return;

            const date = new Date(order.order_date);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

            if (!grouped[monthKey]) {
                grouped[monthKey] = {
                    totalOrders: 0,
                    totalAmount: 0,
                    totalIVA: 0,
                    byStatus: {}
                };
            }

            grouped[monthKey].totalOrders += 1;
            grouped[monthKey].totalAmount += Number(order.total) || 0;
            grouped[monthKey].totalIVA += Number(order.iva) || 0;

            const status = order.status || 'UNKNOWN';
            if (!grouped[monthKey].byStatus[status]) {
                grouped[monthKey].byStatus[status] = {
                    count: 0,
                    amount: 0
                };
            }
            grouped[monthKey].byStatus[status].count += 1;
            grouped[monthKey].byStatus[status].amount += Number(order.total) || 0;
        });

        return grouped;
    }, [orders]);

    // Filter data based on selected month
    const filteredData = useMemo(() => {
        console.log('Filtering data - selectedMonth:', selectedMonth, 'summary:', summary, 'monthlyData:', monthlyData);
        
        if (selectedMonth === 'current') {
            // Calculate current month from orders array
            const now = new Date();
            const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
            const monthData = monthlyData[currentMonthKey];
            
            if (!monthData) {
                return { overall: { total_orders: 0, total_amount: 0, total_iva: 0 }, byStatus: {} };
            }

            return {
                overall: {
                    total_orders: monthData.totalOrders,
                    total_amount: monthData.totalAmount,
                    total_iva: monthData.totalIVA
                },
                byStatus: monthData.byStatus
            };
        } else if (selectedMonth === 'all') {
            // Calculate totals from all months
            const allData = {
                overall: {
                    total_orders: 0,
                    total_amount: 0,
                    total_iva: 0
                },
                byStatus: {}
            };

            Object.values(monthlyData).forEach(monthData => {
                allData.overall.total_orders += monthData.totalOrders;
                allData.overall.total_amount += monthData.totalAmount;
                allData.overall.total_iva += monthData.totalIVA;

                Object.entries(monthData.byStatus).forEach(([status, data]) => {
                    if (!allData.byStatus[status]) {
                        allData.byStatus[status] = { count: 0, amount: 0 };
                    }
                    allData.byStatus[status].count += data.count;
                    allData.byStatus[status].amount += data.amount;
                });
            });

            return allData;
        } else {
            // Specific month selected
            const monthData = monthlyData[selectedMonth];
            if (!monthData) {
                return { overall: {}, byStatus: {} };
            }

            return {
                overall: {
                    total_orders: monthData.totalOrders,
                    total_amount: monthData.totalAmount,
                    total_iva: monthData.totalIVA
                },
                byStatus: monthData.byStatus
            };
        }
    }, [selectedMonth, summary, monthlyData]);
    
    // Debug filtered data
    useEffect(() => {
        console.log('Filtered Data:', filteredData);
    }, [filteredData]);

    // Compute Detalle: paid, adeudado, and saldo a favor.
    // Prefer backend summary aggregates (full dataset) over orders-array calculation (limited to 100).
    const detalleData = useMemo(() => {
        // Use backend summary aggregates if available (accurate across all orders)
        const summaryPaid    = summary?.paid_total    ?? summary?.overall?.paid_total;
        const summaryPending = summary?.pending_total ?? summary?.overall?.pending_total;
        const summaryTotal   = summary?.all?.total    ?? summary?.overall?.all?.total;
        if (summaryPaid !== undefined && summaryPending !== undefined) {
            const paid = summaryPaid;
            const pending = summaryPending;
            const totalAmount = summaryTotal ?? (paid + pending);
            const saldoAFavor = Math.max(0, paid - totalAmount);
            return { paid, pending, saldoAFavor };
        }
        // Fallback: compute from loaded orders array
        if (orders && orders.length > 0) {
            let paid = 0;
            let pending = 0;
            let totalAmount = 0;
            orders.forEach(order => {
                if (order.status === 'CANCELADO') return;
                paid += Number(order.paid_amount) || 0;
                totalAmount += Number(order.total) || 0;
                if (order.payment_status !== 'PAGADO') {
                    pending += Math.max(0, (Number(order.total) || 0) - (Number(order.paid_amount) || 0));
                }
            });
            const saldoAFavor = Math.max(0, paid - totalAmount);
            return { paid, pending, saldoAFavor };
        }
        return { paid: 0, pending: 0, saldoAFavor: 0 };
    }, [orders, summary]);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: 'ARS',
            minimumFractionDigits: 2
        }).format(value || 0);
    };

    const formatMonthLabel = (monthKey) => {
        if (monthKey === 'current') return 'Mes Actual';
        if (monthKey === 'all') return 'Todos';

        const [year, month] = monthKey.split('-');
        const date = new Date(year, parseInt(month) - 1);
        return date.toLocaleDateString('es-AR', { year: 'numeric', month: 'long' });
    };

    const getSupplierLabel = () => {
        if (!selectedSupplierId || selectedSupplierId === 'all') {
            return 'Todos los Proveedores';
        }
        const supplier = suppliers.find(s => s.id === selectedSupplierId);
        return supplier ? supplier.razon_social : 'Proveedor Seleccionado';
    };

    // Month selector component
    const MonthSelector = () => (
        <Select
            value={selectedMonth}
            onChange={setSelectedMonth}
            style={{ width: 140 }}
            size="small"
            disabled={isDemoMode}
        >
            <Option value="current">Mes Actual</Option>
            <Option value="all">Todos</Option>
            {availableMonths.map(month => (
                <Option key={month} value={month}>
                    {formatMonthLabel(month)}
                </Option>
            ))}
        </Select>
    );

    return (
        <div className="supplier-summary-cards">
            <Row gutter={[16, 16]} style={{ display: 'flex', flexWrap: 'wrap' }}
            >
                {/* Supplier Selector Card */}
                <Col style={{ flex: '1', minWidth: 200 }}>
                    <Card size="small" style={{ borderLeft: '4px solid #722ed1', width: '100%' }}>
                        <div className="card-header-row">
                            <div className="card-title-with-icon">
                                <UserOutlined className="card-icon" />
                                <span className="card-title">Proveedor</span>
                            </div>
                        </div>
                        <div className="supplier-selector-content">
                            <Select
                                value={selectedSupplierId || 'all'}
                                onChange={onSupplierChange}
                                style={{ width: '100%' }}
                                placeholder="Seleccionar proveedor"
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().includes(input.toLowerCase())
                                }
                                disabled={isDemoMode}
                            >
                                <Option value="all">Todos</Option>
                                {suppliers.map(supplier => (
                                    <Option key={supplier.id} value={supplier.id}>
                                        {supplier.razon_social}
                                    </Option>
                                ))}
                            </Select>
                            <div className="supplier-label">
                                {getSupplierLabel()}
                            </div>
                            {selectedSupplierId && selectedSupplierId !== 'all' && (
                                <Button
                                    type="primary"
                                    icon={<InboxOutlined />}
                                    onClick={onViewSupplier}
                                    style={{ 
                                        width: '100%', 
                                        marginTop: '12px',
                                        background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                                        borderColor: '#52c41a',
                                        fontWeight: 500
                                    }}
                                >
                                    Ver Proveedor
                                </Button>
                            )}
                        </div>
                    </Card>
                </Col>

                {/* Total Orders Card */}
                <Col style={{ flex: '1', minWidth: 200 }}>
                    <Card size="small" loading={loading} style={{ borderLeft: '4px solid #1890ff', width: '100%' }}>
                        <div className="card-header-row">
                            <div className="card-title-with-icon">
                                <ShoppingCartOutlined className="card-icon" />
                                <span className="card-title">Total Órdenes</span>
                            </div>
                            <MonthSelector />
                        </div>
                        <Statistic
                            value={filteredData.overall?.total_orders || 0}
                            valueStyle={{ color: '#1890ff', fontSize: '24px' }}
                        />
                        <div className="card-details">
                            {Object.entries(filteredData.byStatus || {}).map(([status, data]) => (
                                <div key={status} className="status-detail">
                                    <span className="status-label">{status}:</span>
                                    <span className="status-value">{data.count}</span>
                                </div>
                            ))}
                        </div>
                    </Card>
                </Col>

                {/* Total Amount Card */}
                <Col style={{ flex: '1', minWidth: 200 }}>
                    <Card size="small" loading={loading} style={{ borderLeft: '4px solid #52c41a', width: '100%' }}>
                        <div className="card-header-row">
                            <div className="card-title-with-icon">
                                <DollarOutlined className="card-icon" />
                                <span className="card-title">Monto Total</span>
                            </div>
                            <MonthSelector />
                        </div>
                        <Statistic
                            value={filteredData.overall?.total_amount || 0}
                            precision={0}
                            prefix="$"
                            valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                        />
                        <Text type="secondary" style={{ fontSize: 14 }}>
                            Total en órdenes de compra
                        </Text>
                    </Card>
                </Col>

                {/* Total IVA Card */}
                <Col style={{ flex: '1', minWidth: 200 }}>
                    <Card size="small" loading={loading} style={{ borderLeft: '4px solid #fa8c16', width: '100%' }}>
                        <div className="card-header-row">
                            <div className="card-title-with-icon">
                                <FileTextOutlined className="card-icon" />
                                <span className="card-title">IVA Total</span>
                            </div>
                            <MonthSelector />
                        </div>
                        <Statistic
                            value={filteredData.overall?.total_iva || 0}
                            precision={0}
                            prefix="$"
                            valueStyle={{ color: '#fa8c16', fontSize: '24px' }}
                        />
                        <Text type="secondary" style={{ fontSize: 14 }}>
                            IVA en órdenes de compra
                        </Text>
                    </Card>
                </Col>

                {/* Detalle Card: Pagado + Adeudado */}
                <Col style={{ flex: '1', minWidth: 200 }}>
                    <Card size="small" loading={loading} style={{ borderLeft: '4px solid #d4b106', width: '100%', height: '100%' }}>
                        <div className="card-header-row">
                            <div className="card-title-with-icon">
                                <FileTextOutlined className="card-icon" style={{ color: '#d4b106' }} />
                                <span className="card-title">Detalle</span>
                            </div>
                        </div>
                        <div style={{ marginTop: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                    <CheckSquareOutlined style={{ color: '#52c41a', marginRight: 4 }} />
                                    Monto Pagado
                                </Text>
                                <Text strong style={{ color: '#52c41a', fontSize: 15 }}>
                                    {formatCurrency(detalleData.paid)}
                                </Text>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                    <ExclamationCircleOutlined style={{ color: '#fa541c', marginRight: 4 }} />
                                    Monto Adeudado
                                </Text>
                                <Text strong style={{ color: '#fa541c', fontSize: 15 }}>
                                    {formatCurrency(detalleData.pending)}
                                </Text>
                            </div>
                            {selectedSupplierId && selectedSupplierId !== 'all' && detalleData.saldoAFavor > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, paddingTop: 6, borderTop: '1px dashed #b7eb8f' }}>
                                    <Text type="secondary" style={{ fontSize: 13 }}>
                                        <GiftOutlined style={{ color: '#389e0d', marginRight: 4 }} />
                                        Saldo a Favor
                                    </Text>
                                    <Text strong style={{ color: '#389e0d', fontSize: 15 }}>
                                        {formatCurrency(detalleData.saldoAFavor)}
                                    </Text>
                                </div>
                            )}
                        </div>
                        {(!selectedSupplierId || selectedSupplierId === 'all') && (
                            <Text type="secondary" style={{ fontSize: 11, marginTop: 8, display: 'block' }}>
                                Todos los proveedores
                            </Text>
                        )}
                        {selectedSupplierId && selectedSupplierId !== 'all' && (
                            <Text type="secondary" style={{ fontSize: 11, marginTop: 8, display: 'block' }}>
                                {getSupplierLabel()}
                            </Text>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default SupplierSummaryCards;
